import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../auth/useAuth.js';
import { useT } from '../i18n/useT.js';
import { describeError } from './ErrorMessage.js';

/**
 * Sign out, and only claim to have done so when the server agrees.
 *
 * A failed sign-out stays on the page with a red notification rather than
 * going to /login. The session cookie is httpOnly, so if the server did not end
 * the session nothing else can: showing the login screen would tell someone on
 * a shared machine that they are signed out while the next person to open the
 * address is signed in as them. That is what shipped — see `signOut` in
 * api/client.ts.
 */
export function SignOutButton(): ReactNode {
  const t = useT();
  const { signOut } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  async function handleSignOut(): Promise<void> {
    setBusy(true);
    try {
      await signOut();
    } catch (error) {
      notifications.show({
        color: 'red',
        title: t('shell.signOutFailed'),
        message: describeError(error, t),
      });
      return;
    } finally {
      setBusy(false);
    }
    // Cached ledger data outliving the session would be readable by whoever
    // uses the machine next.
    queryClient.clear();
    await navigate('/login', { replace: true });
  }

  return (
    <Button
      variant="subtle"
      size="compact-sm"
      loading={busy}
      onClick={() => {
        void handleSignOut();
      }}
    >
      {t('shell.signOut')}
    </Button>
  );
}
